import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LABEL = {
  requested: "Awaiting your quote", quoted: "Quote sent", declined: "Declined",
  booked: "Booked", withdrawn: "Withdrawn",
};

/** In-app custom quotes — used for complex commercial, heavy after-builders and unusual work. */
export default function QuotesPanel({ role = "cleaner" }) {
  const [quotes, setQuotes] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/quotes/mine");
    setQuotes(data.quotes);
  };
  useEffect(() => { load(); }, []);

  const respond = async (q) => {
    const d = draft[q.quote_id] || {};
    if (!(parseFloat(d.amount) > 0)) return toast.error("Enter your quote amount");
    setBusy(q.quote_id);
    try {
      await api.post(`/quotes/${q.quote_id}/respond`, {
        amount: parseFloat(d.amount), est_hours: parseFloat(d.est_hours || 3), message: d.message,
      });
      toast.success("Quote sent");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const decline = async (q) => {
    try { await api.post(`/quotes/${q.quote_id}/decline`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!quotes) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
  if (!quotes.length) return (
    <p className="text-muted-foreground py-10 text-center" data-testid="quotes-empty">
      {role === "cleaner"
        ? "No quote requests yet. Complex jobs customers can't book instantly arrive here."
        : "No quotes yet. Ask a cleaner for a quote from their profile when a job is too complex to book instantly."}
    </p>
  );

  return (
    <div className="space-y-4" data-testid="quotes-panel">
      {quotes.map((q) => (
        <div key={q.quote_id} className="bg-card rounded-2xl border border-primary/5 p-5" data-testid={`quote-${q.quote_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-medium inline-flex items-center gap-2">
                <FileText className="h-4 w-4 text-accent" /> {q.service_name}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {role === "cleaner" ? q.customer_name : q.cleaner_name}
                {q.address_city ? ` · ${q.address_city}` : ""}
                {q.preferred_date ? ` · prefers ${q.preferred_date}` : ""}
              </p>
            </div>
            <span className="px-2.5 py-1 rounded-full bg-secondary text-xs font-semibold shrink-0" data-testid={`quote-status-${q.quote_id}`}>
              {LABEL[q.status] || q.status}
            </span>
          </div>
          <p className="text-sm mt-3 whitespace-pre-line">{q.details}</p>
          {q.amount && (
            <p className="text-sm font-semibold mt-3" data-testid={`quote-amount-${q.quote_id}`}>
              Quoted £{q.amount.toFixed(2)} · about {q.est_hours}h on site
            </p>
          )}
          {q.cleaner_message && <p className="text-sm text-muted-foreground mt-1">{q.cleaner_message}</p>}

          {role === "cleaner" && ["requested", "quoted"].includes(q.status) && (
            <div className="mt-4 grid sm:grid-cols-3 gap-3 items-end">
              <label className="text-sm">Your quote (£)
                <input type="number" min="0" step="1" value={draft[q.quote_id]?.amount ?? ""}
                  onChange={(e) => setDraft({ ...draft, [q.quote_id]: { ...draft[q.quote_id], amount: e.target.value } })}
                  className="q-input" data-testid={`quote-amount-input-${q.quote_id}`} />
              </label>
              <label className="text-sm">Time on site (hours)
                <input type="number" min="1" max="12" step="0.5" value={draft[q.quote_id]?.est_hours ?? 3}
                  onChange={(e) => setDraft({ ...draft, [q.quote_id]: { ...draft[q.quote_id], est_hours: e.target.value } })}
                  className="q-input" data-testid={`quote-hours-input-${q.quote_id}`} />
              </label>
              <label className="text-sm">Message (optional)
                <input value={draft[q.quote_id]?.message ?? ""}
                  onChange={(e) => setDraft({ ...draft, [q.quote_id]: { ...draft[q.quote_id], message: e.target.value } })}
                  className="q-input" data-testid={`quote-message-input-${q.quote_id}`} />
              </label>
              <div className="sm:col-span-3 flex gap-3">
                <button onClick={() => respond(q)} disabled={busy === q.quote_id}
                  className="px-5 py-2.5 rounded-full bg-accent text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid={`quote-send-${q.quote_id}`}>
                  {busy === q.quote_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send quote
                </button>
                <button onClick={() => decline(q)} className="px-5 py-2.5 rounded-full border border-border font-medium" data-testid={`quote-decline-${q.quote_id}`}>
                  Can't do this job
                </button>
              </div>
            </div>
          )}

          {role === "customer" && q.status === "quoted" && (
            <button onClick={() => navigate(`/book/${q.cleaner_id}?quote=${q.quote_id}&service=${q.service_slug}`)}
              className="mt-4 px-6 py-2.5 rounded-full bg-accent text-white font-medium"
              data-testid={`quote-accept-${q.quote_id}`}>
              Accept &amp; book at £{q.amount.toFixed(2)}
            </button>
          )}
        </div>
      ))}
      <style>{`.q-input{width:100%;margin-top:0.25rem;padding:0.55rem 0.7rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.q-input:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}
